import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';
import type { Page, Vehicle } from './types';

/**
 * Personel ekranının veri katmanı (`/personel`).
 *
 * "Ekip" ve "Şoförler" ayrı ekranlardan tek "Personel" ekranına toplandı (21.10 kararı).
 * Bu dosya o ekranın kendi verisini taşır: personel listesi, personel defteri
 * (`GET /api/reports/driver-ledger*`) ve cihazlar (`GET /api/driver-devices`).
 *
 * PARA KURALI (CLAUDE.md kural 4): hiçbir toplam/bağıntı burada hesaplanmaz. Defter
 * rakamları sunucudan (`Decimal` → string) gelir; panel yalnız biçimlendirir.
 */

/** Kullanıcı rolü — patron profilini (`user.role !== 'DRIVER'`) ayırmak için gerekir. */
export type PersonnelRole = 'OWNER' | 'MANAGER' | 'DRIVER';

/** Personel satırı: `User` (giriş) + `Driver` (saha profili, araç zimmeti). */
export interface PersonnelDriver {
  id: string;
  isActive: boolean;
  notes: string | null;
  user: {
    id: string;
    fullName: string;
    /** YALNIZ OWNER oturumunda döner; MANAGER'a alan hiç gelmez (KVKK veri azaltma). */
    phone?: string | null;
    isActive: boolean;
    /** YALNIZ OWNER'a döner (NULL = hiç girmedi). MANAGER yalnız aşağıdaki boolean'ları görür. */
    lastLoginAt?: string | null;
    role: PersonnelRole;
  };
  vehicle: { id: string; plate: string; label: string | null } | null;
  /** Şu an süren (açık) sefer; yoksa null. Mesaj hedefi seçerken hangi hesabın sahada olduğunu söyler. */
  openTrip: { id: string; plate: string } | null;
  /** Hesap hiç giriş yapmadıysa true (her rolde döner). */
  neverLoggedIn: boolean;
  /** Son 7 gün içinde girdiyse true (her rolde döner). */
  loggedInWithin7Days: boolean;
}

/** Patron profilinin kullanıcısı DRIVER değildir; PIN'i ve telefon girişi yoktur. */
export function isOwnerProfile(driver: PersonnelDriver): boolean {
  return driver.user.role !== 'DRIVER';
}

/** `GET /api/driver-devices` satırı. `pairingCode`/`keyHash` sunucuda hiç dönmez. */
export interface DriverDevice {
  id: string;
  status: 'PENDING' | 'APPROVED' | 'REVOKED';
  label: string | null;
  driver: { id: string; fullName: string | null } | null;
  createdAt: string;
  approvedAt: string | null;
  /** İlk cihaz otomatik onayı (patron onayı beklenmedi). */
  autoApproved: boolean;
  lastSeenAt: string | null;
  pairingExpiresAt: string | null;
  lockedUntil: string | null;
  failedLoginCount: number;
}

/** Personel defteri — bir günün satırı (sunucudan gelen değerler olduğu gibi). */
export interface PersonnelLedgerDayRow {
  date: string;
  tripCount: number;
  plates: string[];
  deliveredCount: number;
  failedCount: number;
  carriedOverCount: number;
  jugsSold: { kind: string; qty: number }[];
  casesSold: number;
  emptyReceived: number;
  collected: { cash: string; card: string; online: string; credit: string };
  depositCollected: string;
  driverFloat: {
    opening: string;
    collection: string;
    handover: string;
    expense: string;
    other: string;
    closing: string;
  };
}

/** Aralık toplamı — `driverFloat` YOK (sunucu toplamda emaneti bilerek dışarıda bırakır). */
export type PersonnelLedgerTotals = Omit<PersonnelLedgerDayRow, 'date' | 'driverFloat'>;

export interface PersonnelLedgerRange {
  driver: { id: string; fullName: string };
  from: string;
  to: string;
  totals: PersonnelLedgerTotals;
  days: PersonnelLedgerDayRow[];
}

export interface PersonnelLedgerDay {
  date: string;
  driver: { id: string; fullName: string };
  summary: PersonnelLedgerDayRow;
  trips: {
    id: string;
    plate: string;
    status: string;
    openedAt: string;
    closedAt: string | null;
    hasVariance: boolean;
  }[];
  deliveries: {
    id: string;
    deliveredAt: string;
    customer: { id: string; name: string } | null;
    paymentType: string;
    amount: string;
    depositCollected: boolean;
    depositAmount: string;
    jugs: { kind: string; qty: number }[];
    caseQty: number;
    emptyReceived: number;
    leftAtDoor: boolean;
  }[];
}

/** Personel listesi (pasifler dahil — "Geri al" işlemi için görünmeli). */
export function usePersonnelDrivers() {
  return useQuery({
    queryKey: ['personnel-drivers'],
    queryFn: () => api.get<Page<PersonnelDriver>>('/api/drivers?pageSize=100&includeInactive=true'),
  });
}

/** Araç listesi — zimmet kutusu. */
export function usePersonnelVehicles() {
  return useQuery({
    queryKey: ['vehicles'],
    queryFn: () => api.get<Page<Vehicle>>('/api/vehicles'),
  });
}

/**
 * Personel yazma işlemleri.
 *
 * `create`/`resetPin` yanıtları DÜZ METİN PIN taşır ve bu tek fırsattır; PIN kartı
 * gösterilmeden başarı sayılmamalı. `create` telefonu ZORUNLU ister (sunucu 400
 * `driver.phoneRequired` döner); panel boş telefonu göndermez.
 */
export function usePersonnelMutations() {
  const qc = useQueryClient();
  const invalidate = async (): Promise<void> => {
    await qc.invalidateQueries({ queryKey: ['personnel-drivers'] });
    // `drivers` anahtarı başka ekranlarca (seferler, kurulum) kullanılır; eski TeamColumn gibi
    // onu da tazelemek gerekir, yoksa yalnız bu ekran güncel kalır.
    await qc.invalidateQueries({ queryKey: ['drivers'] });
    await qc.invalidateQueries({ queryKey: ['vehicles'] });
  };
  return {
    create: useMutation({
      mutationFn: (v: Record<string, unknown>) =>
        api.post<{ id: string; pin: string; user: { fullName: string } }>('/api/drivers', v),
      onSuccess: invalidate,
    }),
    update: useMutation({
      mutationFn: ({ id, ...v }: { id: string } & Record<string, unknown>) =>
        api.patch(`/api/drivers/${id}`, v),
      onSuccess: invalidate,
    }),
    /**
     * Zimmet atama/kaldırma (`POST /api/drivers/:id/vehicle`). Kaldırma `vehicleId: null` ile
     * yapılır; PATCH şeması null'ı reddettiği için zimmet PATCH'le yazılmaz.
     */
    assignVehicle: useMutation({
      mutationFn: ({ id, vehicleId }: { id: string; vehicleId: string | null }) =>
        api.post(`/api/drivers/${id}/vehicle`, { vehicleId }),
      onSuccess: invalidate,
    }),
    resetPin: useMutation({
      mutationFn: (id: string) =>
        api.post<{ id: string; pin: string }>(`/api/drivers/${id}/reset-pin`, {}),
      onSuccess: invalidate,
    }),
    deactivate: useMutation({
      mutationFn: (id: string) => api.delete(`/api/drivers/${id}`),
      onSuccess: invalidate,
    }),
    /** Patronun kendi personel profilini açar (`POST /api/drivers/owner-profile`). */
    openOwnerProfile: useMutation({
      mutationFn: (v: Record<string, unknown>) => api.post('/api/drivers/owner-profile', v),
      onSuccess: invalidate,
    }),
  };
}

/**
 * Personelin cihazları (`GET /api/driver-devices?driverId=`).
 *
 * Süzgeç sunucudadır: başka kiracının personeli boş liste döner. `driverId` yoksa
 * sorgu ATILMAZ (tüm cihazlar) — panel yalnız bir personelin cihazlarını sorar.
 */
export function useDriverDevices(driverId: string | undefined) {
  return useQuery({
    queryKey: ['driver-devices', driverId],
    queryFn: () => api.get<DriverDevice[]>(`/api/driver-devices?driverId=${driverId}`),
    enabled: !!driverId,
  });
}

/** Cihaz onayı: telefonda girilen 6 haneli eşleştirme kodu onaylanır. */
export function useApproveDevice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { pairingCode: string; driverId: string }) =>
      api.post('/api/driver-devices/approve', v),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['driver-devices'] }),
  });
}

/** Cihaz iptali: onaylı/işlemdeki cihazın oturumları düşer. Kayıt SİLİNMEZ. */
export function useRevokeDevice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post(`/api/driver-devices/${id}/revoke`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['driver-devices'] }),
  });
}

/** Hatalı giriş kilidini kaldırır (yalnız patron; sunucu da denetler). */
export function useUnlockDevice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post(`/api/driver-devices/${id}/unlock`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['driver-devices'] }),
  });
}

/**
 * Personel defteri — aralık (`GET /api/reports/driver-ledger`).
 *
 * Rakamlar sunucudan gelir; panel toplam/bakiye hesaplamaz (kural 1 ve 4).
 */
export function usePersonnelLedger(driverId: string | undefined, from: string, to: string) {
  return useQuery({
    queryKey: ['personnel-ledger', driverId, from, to],
    queryFn: () =>
      api.get<PersonnelLedgerRange>(
        `/api/reports/driver-ledger?driverId=${driverId}&from=${from}&to=${to}`,
      ),
    enabled: !!driverId && from !== '' && to !== '',
  });
}

/** Personel defteri — tek gün (`GET /api/reports/driver-ledger/day`). */
export function usePersonnelLedgerDay(driverId: string | undefined, day: string | null) {
  return useQuery({
    queryKey: ['personnel-ledger-day', driverId, day],
    queryFn: () =>
      api.get<PersonnelLedgerDay>(`/api/reports/driver-ledger/day?driverId=${driverId}&day=${day}`),
    enabled: !!driverId && !!day,
  });
}
